"use client";

import { motion, type Variants } from "motion/react";

// Workspace photo collage (Figma "Final home page" — Rectangles 168–172),
// shown just after "Why Choose Table Space?". Rectangle 168 is the white
// section; 169/170/171/172 are the image tiles, positioned to match the design
// exactly on desktop (a 1440×1222 canvas) and re-flowed into a tidy grid on
// mobile. Tiles cascade in with a staggered rise as the section scrolls in.
const shots = [
  {
    src: "/images/pages/home/collage/worldmark-lounge.webp",
    alt: "Table Space Worldmark centre — lounge and collaboration area, Delhi NCR",
    // Rectangle 169 — top-left, 904×599
    pos: { left: "0%", top: "0%", width: "62.78%", height: "49.02%" },
    mobile: "col-span-2 aspect-[3/2]",
  },
  {
    src: "/images/pages/home/collage/atrium-lounge.webp",
    alt: "Table Space Atrium centre — lounge seating, Delhi NCR",
    // Rectangle 170 — right, offset down, 370×406
    pos: { left: "74.31%", top: "20.95%", width: "25.69%", height: "33.22%" },
    mobile: "aspect-[4/5]",
  },
  {
    src: "/images/pages/home/collage/worldmark-cafe.webp",
    alt: "Table Space Worldmark centre — café seating, Delhi NCR",
    // Rectangle 172 — bottom-left, 246×362
    pos: { left: "0%", top: "61.38%", width: "17.08%", height: "29.62%" },
    mobile: "aspect-[4/5]",
  },
  {
    src: "/images/pages/home/collage/atrium-collab.webp",
    alt: "Table Space Atrium centre — collaboration space, Delhi NCR",
    // Rectangle 171 — bottom-right, 876×461
    pos: { left: "39.17%", top: "59.82%", width: "60.83%", height: "37.73%" },
    mobile: "col-span-2 aspect-[3/2]",
  },
];

const EASE = [0.16, 1, 0.3, 1] as const;
const group: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.16, delayChildren: 0.05 } },
};
const tile: Variants = {
  hidden: { opacity: 0, y: 48, scale: 0.94 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 1.1, ease: EASE } },
};

const imgCls = "h-full w-full object-cover [filter:contrast(1.03)_saturate(1.04)]";

export function WorkspaceCollage({ c = {} }: { c?: Record<string, string> } = {}) {
  return (
    <section className="border-t border-brand-line bg-brand-paper py-16 md:py-24">
      <div>
        {/* desktop — full-bleed asymmetric collage on the design's 1440×1222 canvas */}
        <motion.div
          className="relative hidden aspect-[1440/1222] w-full md:block"
          variants={group}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-12% 0px -12% 0px" }}
        >
          {shots.map((s, i) => (
            <motion.div
              key={s.src}
              className="absolute overflow-hidden will-change-transform"
              style={s.pos}
              variants={tile}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={c[`collage.${i}`]?.trim() || s.src} alt={c[`collage.${i}.alt`]?.trim() || s.alt} data-cms-img={`collage.${i}`} loading="lazy" decoding="async" className={imgCls} />
            </motion.div>
          ))}
        </motion.div>

        {/* mobile — tidy re-flow of the same tiles */}
        <motion.div
          className="grid grid-cols-2 gap-3 px-6 md:hidden"
          variants={group}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-8% 0px -8% 0px" }}
        >
          {shots.map((s, i) => (
            <motion.div
              key={s.src}
              className={`overflow-hidden will-change-transform ${s.mobile}`}
              variants={tile}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={c[`collage.${i}`]?.trim() || s.src} alt={c[`collage.${i}.alt`]?.trim() || s.alt} data-cms-img={`collage.${i}`} loading="lazy" decoding="async" className={imgCls} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
